import React, { useEffect, useMemo, useState } from "react";
import AdminSidebar from "../admin/AdminSidebar";
import { getAdminCourses, addCourse, updateCourse, deleteCourse } from "../api/adminclient";
import AdminTopbar from "./Admintopbar";
import { apiErrorMessage } from "../api/auth";

const emptyForm = { name: "", description: "", active: true };

const PAGE_SIZE = 8;

export default function AdminCourses() {
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [currentPage, setCurrentPage] = useState(1);

  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [formError, setFormError] = useState("");
  const [saving, setSaving] = useState(false);

  const load = () => {
    setLoading(true);
    getAdminCourses()
      .then(({ data }) => setCourses(data))
      .catch((err) => setError(apiErrorMessage(err, "Could not load courses.")))
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  const totalPages = Math.max(1, Math.ceil(courses.length / PAGE_SIZE));

  // Clamp currentPage if a delete shrinks the list below the current page
  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(totalPages);
  }, [currentPage, totalPages]);

  const paginated = useMemo(() => {
    const start = (currentPage - 1) * PAGE_SIZE;
    return courses.slice(start, start + PAGE_SIZE);
  }, [courses, currentPage]);

  const goToPage = (page) => {
    if (page < 1 || page > totalPages) return;
    setCurrentPage(page);
  };

  const openAdd = () => {
    setEditingId(null);
    setForm(emptyForm);
    setFormError("");
    setShowForm(true);
  };

  const openEdit = (course) => {
    setEditingId(course.id);
    setForm({
      name: course.name,
      description: course.description || "",
      active: course.active !== false,
    });
    setFormError("");
    setShowForm(true);
  };

  const save = async () => {
    setFormError("");
    if (!form.name.trim()) {
      setFormError("Course name is required.");
      return;
    }
    setSaving(true);
    try {
      if (editingId) {
        await updateCourse(editingId, form);
      } else {
        await addCourse(form);
      }
      setShowForm(false);
      load();
    } catch (err) {
      setFormError(apiErrorMessage(err, "Could not save course."));
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (course) => {
    try {
      await updateCourse(course.id, { active: course.active === false });
      load();
    } catch (err) {
      setError(apiErrorMessage(err, "Could not update course status."));
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this course? This cannot be undone.")) return;
    try {
      await deleteCourse(id);
      load();
    } catch (err) {
      setError(apiErrorMessage(err, "Could not delete course."));
    }
  };

  return (
    <div className="admin-page-full d-flex">
      <AdminSidebar />
      <div className="admin-content">
        <AdminTopbar/>
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h2 className="h5 fw-bold mb-0">Courses</h2>
          <button className="btn btn-primary" onClick={openAdd}>
            <i className="bi bi-plus-lg me-1" /> Add Course
          </button>
        </div>

        {error && <div className="alert alert-warning py-2 small">{error}</div>}

        <div className="admin-card p-3 p-md-4">
          {loading && <p className="text-muted mb-0">Loading courses...</p>}

          {!loading && courses.length === 0 && (
            <p className="text-muted small mb-0">No courses yet.</p>
          )}

          {!loading && courses.length > 0 && (
            <>
              <div className="table-responsive">
                <table className="table align-middle admin-table mb-0">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Name</th>
                      <th>Description</th>
                      <th>Status</th>
                      <th className="text-end">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginated.map((c, i) => {
                      const rowNumber = (currentPage - 1) * PAGE_SIZE + i + 1;
                      return (
                        <tr key={c.id}>
                          <td className="text-muted">{rowNumber}</td>
                          <td className="fw-semibold"><i className="bi bi-journal-text me-2 text-primary" />{c.name}</td>
                          <td className="text-muted small">{c.description || "—"}</td>
                          <td>
                            <div className="form-check form-switch">
                              <input
                                className="form-check-input"
                                type="checkbox"
                                checked={c.active !== false}
                                onChange={() => toggleActive(c)}
                              />
                            </div>
                          </td>
                          <td className="text-end">
                            <button className="btn btn-sm btn-outline-secondary me-1" onClick={() => openEdit(c)}>
                              <i className="bi bi-pencil" />
                            </button>
                            <button className="btn btn-sm btn-outline-danger" onClick={() => handleDelete(c.id)}>
                              <i className="bi bi-trash" />
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <div className="d-flex justify-content-between align-items-center mt-3 flex-wrap gap-2">
                <p className="text-muted small mb-0">
                  Showing {(currentPage - 1) * PAGE_SIZE + 1}
                  –{Math.min(currentPage * PAGE_SIZE, courses.length)} of {courses.length}
                </p>

                <nav aria-label="Courses pagination">
                  <ul className="pagination pagination-sm mb-0">
                    <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
                      <button className="page-link" onClick={() => goToPage(currentPage - 1)}>
                        <i className="bi bi-chevron-left" />
                      </button>
                    </li>

                    {Array.from({ length: totalPages }, (_, idx) => idx + 1).map((page) => (
                      <li key={page} className={`page-item ${page === currentPage ? "active" : ""}`}>
                        <button className="page-link" onClick={() => goToPage(page)}>
                          {page}
                        </button>
                      </li>
                    ))}

                    <li className={`page-item ${currentPage === totalPages ? "disabled" : ""}`}>
                      <button className="page-link" onClick={() => goToPage(currentPage + 1)}>
                        <i className="bi bi-chevron-right" />
                      </button>
                    </li>
                  </ul>
                </nav>
              </div>
            </>
          )}
        </div>
      </div>

      {showForm && (
        <div className="modal d-block" tabIndex={-1} style={{ background: "rgba(0,0,0,0.45)" }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">{editingId ? "Edit Course" : "Add Course"}</h5>
                <button className="btn-close" onClick={() => setShowForm(false)} />
              </div>
              <div className="modal-body">
                <div className="mb-3">
                  <label className="form-label small text-muted">Course Name</label>
                  <input
                    className="form-control"
                    value={form.name}
                    onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                  />
                </div>
                <div className="mb-2">
                  <label className="form-label small text-muted">Description</label>
                  <textarea
                    className="form-control"
                    rows={3}
                    value={form.description}
                    onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                  />
                </div>
                {formError && <div className="text-danger small mt-2">{formError}</div>}
              </div>
              <div className="modal-footer">
                <button className="btn btn-outline-secondary" onClick={() => setShowForm(false)}>Cancel</button>
                <button className="btn btn-primary" onClick={save} disabled={saving}>
                  {saving ? "Saving..." : "Save"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}